import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { ATTRIBUTION_NOTICE } from '../../utils/attribution';
import { createAuthenticatedApiClient, setAuthHeader } from '../../config/api';

// Create authenticated API client for profile operations
const apiClient = createAuthenticatedApiClient();

/**
 * Component for displaying and editing user profile information
 */
const ProfilePage: React.FC = () => {
  const { currentUser, updateUserProfile } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    aliasName: '',
    zipcode: '',
    emailPreferences: {
      newsletter: false,
      notifyOnInspiration: false,
      reminders: 'weekly',
      unsubscribed: false
    }
  });
  
  // UI state
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Initialize form data from current user profile
  useEffect(() => {
    if (currentUser) {
      setFormData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        aliasName: currentUser.aliasName || '',
        zipcode: currentUser.location?.zipcode || '',
        emailPreferences: {
          newsletter: currentUser.emailPreferences?.newsletter || false,
          notifyOnInspiration: currentUser.emailPreferences?.notifyOnInspiration || false,
          reminders: currentUser.emailPreferences?.reminders || 'weekly',
          unsubscribed: currentUser.emailPreferences?.unsubscribed || false
        }
      });
    }
  }, [currentUser]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      // Handle nested objects like emailPreferences
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      // Handle top-level fields
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    // Reset messages when toggling edit mode
    setError(null);
    setSuccess(null);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      
      // Prepare data for API
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        aliasName: formData.aliasName,
        location: {
          zipcode: formData.zipcode
        },
        emailPreferences: formData.emailPreferences
      };
      
      // Update profile via API using the configured apiClient
      const response = await apiClient.put<{ success: boolean; data: any }>('/contributor/profile', updateData);
      
      // Update local context if successful
      if (response.data && response.data.success && updateUserProfile) {
        updateUserProfile(response.data.data);
      }
      
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset form data to current user data
    if (currentUser) {
      setFormData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        aliasName: currentUser.aliasName || '',
        zipcode: currentUser.location?.zipcode || '',
        emailPreferences: {
          newsletter: currentUser.emailPreferences?.newsletter || false,
          notifyOnInspiration: currentUser.emailPreferences?.notifyOnInspiration || false,
          reminders: currentUser.emailPreferences?.reminders || 'weekly',
          unsubscribed: currentUser.emailPreferences?.unsubscribed || false
        }
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  // Loading state
  if (!currentUser) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading profile information...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col">
          <h1 className="mb-0">My Profile</h1>
          <p className="text-muted">View and edit your personal information</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError(null)} 
            aria-label="Close"
          ></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setSuccess(null)} 
            aria-label="Close"
          ></button>
        </div>
      )}

      <div className="row">
        <div className="col-lg-8">
          {/* Profile Information Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-header d-flex justify-content-between align-items-center bg-white py-3">
              <h5 className="mb-0">Personal Information</h5>
              <button 
                className={`btn ${isEditing ? "btn-outline-secondary" : "btn-outline-primary"} btn-sm`}
                onClick={toggleEditMode}
                disabled={isLoading}
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>
            <div className="card-body">
              {isEditing ? (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">First Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Public Display Name (Alias)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="aliasName"
                      value={formData.aliasName}
                      onChange={handleChange}
                      placeholder="How you want to be identified publicly (optional)"
                    />
                    <small className="form-text text-muted">
                      {ATTRIBUTION_NOTICE}
                    </small>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Zipcode</label>
                    <input
                      type="text"
                      className="form-control"
                      name="zipcode"
                      value={formData.zipcode}
                      onChange={handleChange}
                      placeholder="Enter your zipcode"
                    />
                  </div>
                  
                  <h5 className="mt-4 mb-3">Email Preferences</h5>
                  
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="newsletter"
                      name="emailPreferences.newsletter"
                      checked={formData.emailPreferences.newsletter}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="newsletter">Newsletter</label>
                    <small className="d-block form-text text-muted">
                      Receive our monthly newsletter with updates and stories.
                    </small>
                  </div>
                  
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="notifyOnInspiration"
                      name="emailPreferences.notifyOnInspiration"
                      checked={formData.emailPreferences.notifyOnInspiration}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="notifyOnInspiration">
                      Notify me when my notes inspire others
                    </label>
                    <small className="d-block form-text text-muted">
                      Get notified when someone creates a note after viewing yours.
                    </small>
                  </div>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <button 
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : 'Save Changes'}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">First Name:</div>
                    <div className="col">{currentUser.firstName || '-'}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Last Name:</div>
                    <div className="col">{currentUser.lastName || '-'}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Email:</div>
                    <div className="col">{currentUser.email || '-'}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Public Display Name:</div>
                    <div className="col">{currentUser.aliasName || '-'}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Location:</div>
                    <div className="col">
                      {currentUser.location ? (
                        <>
                          {currentUser.location.city && currentUser.location.state 
                            ? `${currentUser.location.city}, ${currentUser.location.state}` 
                            : currentUser.location.zipcode}
                        </>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Role:</div>
                    <div className="col">{currentUser.role}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Subscription:</div>
                    <div className="col">{currentUser.subscriptionTier}</div>
                  </div>
                  
                  <h5 className="mt-4 mb-3">Email Preferences</h5>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Newsletter:</div>
                    <div className="col">{currentUser.emailPreferences?.newsletter ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Inspiration Notifications:</div>
                    <div className="col">{currentUser.emailPreferences?.notifyOnInspiration ? 'Yes' : 'No'}</div>
                  </div>
                  
                  <div className="mt-4">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setIsEditing(true)}
                      aria-label="Edit profile"
                    >
                      <i className="fas fa-edit me-2"></i>
                      Edit Profile
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Donation History Section */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Donation History</h5>
              <span className="badge bg-info">Separate Collection</span>
            </div>
            <div className="card-body">
              <p>View your contribution history and impact. Your donations are stored separately from your profile for better data management.</p>
              <Link 
                to="/donations" 
                className="btn btn-outline-secondary"
                aria-label="View donation history"
              >
                <i className="fas fa-heart me-2"></i>View Donation History
              </Link>
            </div>
          </div>
        </div>
        
        <div className="col-lg-4">
          {/* Account Actions */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0">Account Actions</h5>
            </div>
            <div className="card-body">
              <Link to="/contributor/my-notes" className="btn btn-outline-primary d-block mb-3">
                My Notes
              </Link>
              <Link to="/contributor/my-displays" className="btn btn-outline-primary d-block mb-3">
                My Displays
              </Link>
            </div>
          </div>
          
          {/* Delete Account Section */}
          <div className="card shadow-sm border-danger">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0 text-danger">Danger Zone</h5>
            </div>
            <div className="card-body">
              <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
              <button className="btn btn-outline-danger">Delete Account</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 